import React from 'react';
import {
  Box, Typography, TextField, Switch, Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { zhTW } from 'date-fns/locale';
import InfoIcon from '@mui/icons-material/Info';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import QuizIcon from '@mui/icons-material/Quiz';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PhoneIcon from '@mui/icons-material/Phone';
import GroupIcon from '@mui/icons-material/Group';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ChairIcon from '@mui/icons-material/Chair';

const ICON_MAP = {
  person: <PersonIcon sx={{ fontSize: 14 }} />,
  event: <EventIcon sx={{ fontSize: 14 }} />,
  schedule: <ScheduleIcon sx={{ fontSize: 14 }} />,
  phone: <PhoneIcon sx={{ fontSize: 14 }} />,
  group: <GroupIcon sx={{ fontSize: 14 }} />,
  child_care: <ChildCareIcon sx={{ fontSize: 14 }} />,
  location_on: <LocationOnIcon sx={{ fontSize: 14 }} />,
  description: <DescriptionIcon sx={{ fontSize: 14 }} />,
  restaurant: <RestaurantIcon sx={{ fontSize: 14 }} />,
  chair: <ChairIcon sx={{ fontSize: 14 }} />,
  text_fields: <TextFieldsIcon sx={{ fontSize: 14 }} />,
};

// 變數 key → 顯示標籤
const VARIABLE_LABELS = {
  restaurant_name: '公司名稱 (顯示在開場白)',
  company_name: '公司名稱 (顯示在開場白)',
  season_start: '開放預訂起始日',
  season_end: '開放預訂結束日',
  // feature_plans/082 §3.5 — per-company 營業時間 + 訂位人數限制
  lunch_start: '中午開始時間',
  lunch_end: '中午結束時間',
  dinner_start: '晚上開始時間',
  dinner_end: '晚上結束時間',
  min_party_size: '最少訂位人數',
  max_party_size: '最多訂位人數',
};

// 系統內部變數（不顯示給使用者）
const HIDDEN_VARS = ['company_id'];

// 永遠顯示的「已知欄位」— 即使 flowDefinition.variables 還沒有這個 key 也要 render
// 為空 input。使用者填寫後會透過 PUT /api/voice-flows/:id/script 新增進 JSONB。
// 順序也決定了表單顯示順序。
const ALWAYS_SHOW_VARS = [
  'restaurant_name',
  'company_name',
  'season_start',
  'season_end',
  'lunch_start',
  'lunch_end',
  'dinner_start',
  'dinner_end',
  'min_party_size',
  'max_party_size',
];

// 顯示順序：ALWAYS_SHOW_VARS 中的欄位優先依序顯示，其餘維持原順序追加。
const VARIABLE_ORDER = ALWAYS_SHOW_VARS;

// 哪些變數應該以 DatePicker 編輯（值為 'YYYY-MM-DD' 字串）
const DATE_VAR_KEYS = ['season_start', 'season_end'];

// HH:MM 時間欄位（native <input type="time">）
const TIME_VAR_KEYS = ['lunch_start', 'lunch_end', 'dinner_start', 'dinner_end'];

// 整數欄位（人數）
const NUMBER_VAR_KEYS = ['min_party_size', 'max_party_size'];

// 字串 ↔ Date — 跟 ConstraintEditDialog 的轉換相同
const isoToDate = (s) => {
  if (!s || typeof s !== 'string') return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const dateToIso = (d) => {
  if (!d || isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const FlowSettingsPanel = ({
  flowName,
  triggerConditions,
  variables,
  slots,
  faqInterrupt,
  onVariableChange,
  onFaqInterruptChange,
  readOnly = false,
}) => {
  const present = variables
    ? Object.entries(variables).filter(([key]) => !HIDDEN_VARS.includes(key))
    : [];

  // 把「已知必顯示」欄位 union 進來：若 variables 沒有，補 '' 預設值，
  // 讓使用者直接在 panel 填入；按儲存後 backend applyScriptEdits 會把新 key 寫入 JSONB。
  const presentKeys = new Set(present.map(([k]) => k));
  const fillIns = ALWAYS_SHOW_VARS
    .filter((k) => !presentKeys.has(k) && !HIDDEN_VARS.includes(k))
    .map((k) => [k, '']);

  const allVisibleVars = [...present, ...fillIns];

  // 應用顯示順序：在 VARIABLE_ORDER 中的優先依序顯示，其餘維持原順序追加。
  const orderIndex = (key) => {
    const idx = VARIABLE_ORDER.indexOf(key);
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
  };
  const displayVariables = [...allVisibleVars].sort(
    ([a], [b]) => orderIndex(a) - orderIndex(b)
  );

  const dialedNumbers = triggerConditions?.dialed_numbers || [];

  const renderVariableEditor = (key, value) => {
    if (DATE_VAR_KEYS.includes(key)) {
      return (
        <DatePicker
          value={isoToDate(value)}
          onChange={(d) => onVariableChange(key, dateToIso(d))}
          format="yyyy / MM / dd"
          disabled={readOnly}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              sx: { '& .MuiInputBase-root': { fontSize: '13px', bgcolor: '#fff' } },
            },
          }}
        />
      );
    }
    if (TIME_VAR_KEYS.includes(key)) {
      return (
        <TextField
          type="time"
          value={value || ''}
          onChange={(e) => onVariableChange(key, e.target.value)}
          size="small"
          fullWidth
          disabled={readOnly}
          inputProps={{ step: 300 }} // 5 分鐘
          sx={{ '& .MuiInputBase-root': { fontSize: '13px', bgcolor: '#fff' } }}
        />
      );
    }
    if (NUMBER_VAR_KEYS.includes(key)) {
      return (
        <TextField
          type="number"
          value={value === '' || value === null || value === undefined ? '' : value}
          onChange={(e) => {
            const v = e.target.value;
            // 允許清空；非空時轉為整數
            onVariableChange(key, v === '' ? '' : parseInt(v, 10));
          }}
          size="small"
          fullWidth
          disabled={readOnly}
          inputProps={{ min: 1, max: 99, step: 1 }}
          sx={{ '& .MuiInputBase-root': { fontSize: '13px', bgcolor: '#fff' } }}
        />
      );
    }
    return (
      <TextField
        value={value || ''}
        onChange={(e) => onVariableChange(key, e.target.value)}
        size="small"
        fullWidth
        disabled={readOnly}
        sx={{ '& .MuiInputBase-root': { fontSize: '13px', bgcolor: '#fff' } }}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={zhTW}>
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: '#fafafa' }}>
      {/* 基本資訊 */}
      <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #eee' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
          <InfoIcon sx={{ fontSize: 16, color: '#888' }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '12px' }}>
            基本資訊
          </Typography>
        </Box>

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5, fontSize: '12px' }}>腳本名稱</Typography>
          <TextField
            value={flowName || ''}
            size="small"
            fullWidth
            disabled
            sx={{ '& .MuiInputBase-root': { fontSize: '13px', bgcolor: '#fff' } }}
          />
        </Box>

        {displayVariables.map(([key, value]) => (
          <Box key={key} sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5, fontSize: '12px' }}>
              {VARIABLE_LABELS[key] || key}
            </Typography>
            {renderVariableEditor(key, value)}
          </Box>
        ))}

        {dialedNumbers.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5, fontSize: '12px' }}>觸發號碼</Typography>
            <TextField
              value={dialedNumbers.join(', ')}
              size="small"
              fullWidth
              disabled
              sx={{ '& .MuiInputBase-root': { fontSize: '13px', bgcolor: '#f5f5f5' } }}
            />
          </Box>
        )}
      </Box>

      {/* 帶入欄位 */}
      <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #eee' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <TextFieldsIcon sx={{ fontSize: 16, color: '#888' }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '12px' }}>
            帶入欄位
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 1, fontSize: '12px' }}>
          AI 在通話中自動收集的資料欄位
        </Typography>

        {(!slots || slots.length === 0) ? (
          <Typography variant="caption" color="text.secondary">無欄位定義</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
            {slots.map((slot) => (
              <Chip
                key={slot.key}
                icon={ICON_MAP[slot.icon] || ICON_MAP.text_fields}
                label={slot.label}
                size="small"
                sx={{
                  height: 28,
                  fontSize: '12px',
                  bgcolor: '#e8f4fd',
                  color: '#145da0',
                  border: '1px solid #b8d8f0',
                  '& .MuiChip-icon': { color: '#145da0' },
                  cursor: 'default',
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* FAQ 智能回覆 */}
      <Box sx={{ px: 2.5, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
          <QuizIcon sx={{ fontSize: 16, color: '#888' }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '12px' }}>
            FAQ 智能回覆
          </Typography>
        </Box>

        {faqInterrupt ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '13px' }}>啟用 FAQ 中斷回覆</Typography>
              <Switch
                checked={faqInterrupt.enabled}
                onChange={(e) => onFaqInterruptChange('enabled', e.target.checked)}
                disabled={readOnly}
                size="small"
              />
            </Box>
            {faqInterrupt.enabled && (
              <>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5, fontSize: '12px' }}>
                    每節點最大中斷次數
                  </Typography>
                  <TextField
                    type="number"
                    value={faqInterrupt.maxPerNode || 3}
                    onChange={(e) => onFaqInterruptChange('maxPerNode', parseInt(e.target.value) || 0)}
                    size="small"
                    fullWidth
                    disabled={readOnly}
                    inputProps={{ min: 0, max: 10 }}
                    sx={{ '& .MuiInputBase-root': { fontSize: '13px', bgcolor: '#fff' } }}
                  />
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5, fontSize: '12px' }}>
                    恢復對話語句
                  </Typography>
                  <TextField
                    value={faqInterrupt.resumeText || ''}
                    onChange={(e) => onFaqInterruptChange('resumeText', e.target.value)}
                    size="small"
                    fullWidth
                    disabled={readOnly}
                    sx={{ '& .MuiInputBase-root': { fontSize: '13px', bgcolor: '#fff' } }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: '#999', fontSize: '11px' }}>
                  通話中客戶問到 FAQ 問題時，AI 自動回答後繼續原本流程
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">此流程不支援 FAQ 中斷</Typography>
        )}
      </Box>
    </Box>
    </LocalizationProvider>
  );
};

export default FlowSettingsPanel;
